import { atom, noWait } from "recoil";
import {
  selectorFamilyWithoutCache as selectorFamily,
  selectorWithoutCache as selector,
} from "util/RecoilWithoutCache";
import { CurrentMarginSelector } from "./Margin";
import { calOpenCost, Zero } from "helper/formula/MixContractFormula";
import {
  ContractPositionLevelList,
  createDefaultMixContractInfo,
  CurrentBusinessLineContractListState,
  CurrentBusinessLineContractPositionLevelListState,
  getLevel,
  MixContractInfoMapSelector,
  MixContractInfoState,
  ContractIsUnionModeSelector,
} from "./Contract";
import { MixContractInfoStateSelector as MixStrategyInfoState } from "./Strategy";
import {
  MixContractFixedUserAccountInfo,
  MixContractUserAccountPushInfo,
} from "../entity";
import { FixedOrderMaintainValueStateSelector } from "store/data/Order";

// import {
//     createEmptyMixTickerInfo,
//     MixContractAllTickersMap,
//     MixContractTickerKeyMap
// } from './Ticker'
import {
  CurrentContractCanCloseSelector,
  MixContractBaseFixedPositionListSelector,
  MixContractPositionListState,
  MixContractPositionStatisticSelector,
} from "./Position";
import { Decimal } from "decimal.js";
import {
  formatCurrentOrders,
  MixContractCurrentOrderFixedData,
  MixContractCurrentOrdersState,
  MixContractCurrentOrderStatisticSelector,
} from "./Order";
import StatisticResult from "util/StatisticResult";
import {
  MixContractDelegateType,
  MixContractHoldSide,
  MixContractMarginMode,
} from "constants/MixContractGlobal";
import { findLast as findLastIndex } from "lodash-es";
import { getTokenPriceByTokenId, MixContractAllTokensSelector } from "./Token";
import { MixOneWayCalculateOrdersMargin } from "./OneWayPosition";
import { UserPreferenceSettingState } from "./User";
import { PositionModeEnum, USDT_ASSET_MODE_TYPE } from "constants/vars";
import delegateType2Holdside from "helper/delegateType2Holdside";
// 状态
import {
  OPOnlyReducePositionPresetState,
  OPTypeState,
} from "components/OrderPlacer/store/atom";
import { getContractConfig } from "./PositionComputed";
import {
  calcalteLongNewDCanUseValue,
  calcCompensateMargin,
} from "./AccountComputed";
import { calculateRate, createRefKeeper } from "util/index";
import { OrderTypeEnum } from "constants/order";
import { CurrentBusinessLineState } from "./Base";
import { BusinessLineToQuoteToken } from "../../constants/base";
import { MinifiedTickerListAtom, getTicker } from "../v2/Ticker";

function createEmptyAccountInfo(): MixContractFixedUserAccountInfo {
  return {
    accountId: null,
    available: 0,
    bonus: "0",
    businessLine: undefined,
    crossMarginRate: 0,
    crossMaxAvailable: 0,
    crossRiskRate: 0,
    dTotalUpnl: Zero,
    dTotalUsed: Zero,
    dTotalPnl: Zero,
    dProfits: Zero,
    dCanUse: Zero,
    dCanUseUI: Zero,
    dCanUseValue: Zero,
    dRiskRate: Zero,
    dFreeze: Zero,
    dCanAppend: Zero,
    equity: 0,
    fixedMaxAvailable: 0,
    id: 0,
    key: "",
    locked: 0,
    maxTransferOut: 0,
    secondBusinessLine: "N/A",
    settleTokenId: "",
    tokenId: "",
    usedBalance: 0,
    maintainPart: Zero,
  };
}

export const MixContractBalanceListState = atom<
  MixContractUserAccountPushInfo[]
>({
  key: "balance-list-state-original",
  default: [],
});

export const generateUniKey = (tokenId: string, symbolId: string) => {
  return `${tokenId}_${symbolId}`;
};
//inverse 注意 ！！！ 如果保证金id里面有 下划线 就会出问题
export const inverseGenerateUniKey = (uniKey: string) => {
  let index = uniKey.indexOf("_");
  let tokenId = uniKey.substring(0, index);
  let symbolId = uniKey.substring(index + 1);
  return { tokenId, symbolId };
};

// 联合保证金模式下--各资产总额
export const MixContractUnionAssetPrice = selector({
  key: "MixContractUnionAssetPrice",
  get: ({ get }) => {
    const balanceList = get(MixContractBalanceListState);
    const isUnionMode = get(ContractIsUnionModeSelector);
    const businessLine = get(CurrentBusinessLineState);

    let unionAssetAvailable = 0;
    let ummr = 1;
    let debtMRatio = 1;
    let debtLimit = 0;

    if (isUnionMode) {
      const allMarginPriceToken = get(MixContractAllTokensSelector);

      const unionMarginConfig = {};
      allMarginPriceToken.forEach((x) => {
        // todo 后续若开放其他业务线，则需要将 ['USDT', 'SUSDT'] 的判断去除
        if (["USDT", "SUSDT"].includes(x.quoteTokenId)) {
          unionMarginConfig[x.baseTokenId] = x;
        }
        if (x.quoteTokenId === BusinessLineToQuoteToken[businessLine]) {
          debtLimit = x.debtLimit;
        }
      });

      balanceList.forEach((x) => {
        const marginConfig = unionMarginConfig[x.tokenId];
        if (marginConfig) {
          const { indexPrice, mcdr } = marginConfig;
          debtMRatio = marginConfig.debtMRatio;
          ummr = marginConfig.ummr;
          const { available } = x;
          const p = new Decimal(available).mul(indexPrice).toNumber();
          const marginPrice = calculateRate(p, mcdr);

          unionAssetAvailable += Number(marginPrice);
        }
      });
    }

    return {
      unionAssetAvailable,
      ummr,
      debtMRatio,
      debtLimit,
    };
  },
});

// 联合保证金模式下--负债
export const MixContractUnionUsdtDebt = selector({
  key: "MixContractUnionUsdtDebt",
  get: ({ get }) => {
    const businessLine = get(CurrentBusinessLineState);
    const currentToken = BusinessLineToQuoteToken[businessLine];

    const isUnionAssetMode = get(ContractIsUnionModeSelector);
    const balanceList = get(MixContractBalanceListState);
    const { tokenTotalUpnl } = get(MixContractPositionStatisticSelector);
    if (isUnionAssetMode) {
      const usdtUnrealizedPnL = tokenTotalUpnl.get(currentToken);
      const usdtBalance = balanceList.find((x) => x.tokenId === currentToken);

      const debts = usdtUnrealizedPnL.add(usdtBalance?.available || 0);
      return debts.comparedTo(0) === -1 ? debts.abs().toNumber() : 0;
    }
    return 0;
  },
});

export const MixContractBalanceListSelector = selector({
  key: "balance-list-selector-fixed",
  get: ({ get }) => {
    // 原始资产信息
    const balanceList = get(MixContractBalanceListState);
    const positionModeSet = get(UserPreferenceSettingState).positionModeSet;

    const { unionAssetAvailable, ummr, debtMRatio } = get(
      MixContractUnionAssetPrice
    );
    const usdtDebt = get(MixContractUnionUsdtDebt);
    const isUnionMode = get(ContractIsUnionModeSelector);

    // 仓位聚合的数据
    const {
      tokenTotalUpnl,
      tokenTotalPnl,
      tokenTotalFixedPositionMargin,
      tokenCrossedMaintain,
      tokenTotalCrossedPositionUnrealizedProfit,
      tokenCrossedMargin,
    } = get(MixContractPositionStatisticSelector);

    // 委托聚合的数据
    const {
      tokenTotalFixedOpenCost,
      tokenTotalCrossedOpenCost,
      tokenTotalCrossedOpenMaintain,
    } = get(MixContractCurrentOrderStatisticSelector);

    //用下面的替换了 这个不知道为什么取不到保证金币价
    // const tokenTotalFixedOrderMaintainValue = get(FixedOrderMaintainValueSelector)
    const tokenTotalFixedOrderMaintainValue = get(
      FixedOrderMaintainValueStateSelector
    );

    const { tokenIdTotalMargin, tokenIdFixedTotalMargin, tokenIdTotalValue } =
      get(MixOneWayCalculateOrdersMargin);

    // 第一次循环资产信息，附加"总"信息
    return balanceList.map<MixContractFixedUserAccountInfo>((account) => {
      const tokenId = account.tokenId;
      const bp = get(getTokenPriceByTokenId(tokenId)).indexPrice;

      let dAvailable = new Decimal(account.available);

      // 只有保证金为 USDT 和 SUSDT
      // todo 后续若开放其他业务线，则需要将 ['USDT', 'SUSDT'] 的判断去除
      if (["USDT", "SUSDT"].includes(tokenId)) {
        dAvailable = dAvailable.add(unionAssetAvailable);
      }

      // 全仓逐仓加一起
      const dTotalUpnl = tokenTotalUpnl.get(tokenId);
      const dTotalPnl = tokenTotalPnl.get(tokenId);

      const totalCrossedMargin = tokenCrossedMargin.get(tokenId);
      const totalCrossedOpenCost = tokenTotalCrossedOpenCost.get(tokenId);
      const totalFixedMargin = tokenTotalFixedPositionMargin.get(tokenId);
      const totalFixedOpenCost = tokenTotalFixedOpenCost.get(tokenId);

      const totalCrossedUpnl =
        tokenTotalCrossedPositionUnrealizedProfit.get(tokenId);
      const totalCrossedMaintain = tokenCrossedMaintain.get(tokenId);
      const totalCrossedOpenMaintain =
        tokenTotalCrossedOpenMaintain.get(tokenId);
      // 不能混到一起取大头 要单币种取大头 这里相加
      // 币对占用保证金
      let totalMargin = tokenIdTotalMargin.get(tokenId); // 按保证金比重拿的
      // 逐仓币对占用仓位保证金
      let fixedTotalMargin = tokenIdFixedTotalMargin.get(tokenId);
      // 账户权益：available+全仓未实现盈亏+逐仓未实现盈亏+逐仓保证金
      // 逐仓保证金和全仓保证金相互隔离，全仓保证金又是共担风险，所以全仓保证金并没有从available中剥离
      let dProfits = dAvailable.add(dTotalUpnl).add(totalFixedMargin);

      // 可用：available-总开仓冻结（逐仓全仓）-全仓保证金+（总未实现盈亏-逐仓未实现盈利）
      // 2-24：不再包含逐仓的未实现亏损，不再扣除逐仓亏损
      // 2-24：available-总开仓冻结（逐仓全仓）-全仓保证金+ 全仓总未实现盈亏
      let dCanUse = Zero;
      if (positionModeSet === PositionModeEnum.ONE_WAY) {
        /**
         * {
         *     "dAvailable": 766.3320348,
         *     "totalMargin": 1458.4988,
         *     "totalCrossedUpnl": 695.942,
         *     "totalFixedMargin": 0,
         *     "dCanUse": 3.7752348
         * }
         * */

        dCanUse = dAvailable
          .sub(totalMargin) //所有币对的占用保证金
          .add(totalCrossedUpnl)
          .add(totalFixedMargin);
      } else {
        dCanUse = dAvailable
          .sub(totalCrossedOpenCost.add(totalFixedOpenCost))
          .sub(totalCrossedMargin)
          .add(totalCrossedUpnl);
      }

      let dCanAppend = Decimal.max(
        dCanUse.sub(totalCrossedUpnl.greaterThan(0) ? totalCrossedUpnl : 0),
        Zero
      );

      let dTotalUsed;
      if (positionModeSet === PositionModeEnum.ONE_WAY) {
        dTotalUsed = totalMargin;
      } else {
        // 已用：全仓仓位保证金+全仓开仓委托成本+逐仓仓位保证金+逐仓开仓委托成本
        dTotalUsed = totalCrossedMargin
          .add(totalCrossedOpenCost)
          .add(totalFixedMargin)
          .add(totalFixedOpenCost);
      }

      // 冻结：仅包含全仓开仓成本+逐仓开仓成本
      let dFreeze = totalCrossedOpenCost.add(totalFixedOpenCost);
      let fixedOrderMaintainValue =
        tokenTotalFixedOrderMaintainValue.get(tokenId);
      //根据是否单向持仓修改影响保证金比率里面的逐仓委托维持保证金

      let maintainPart = new Decimal(0);
      let oneWayMaintainPart = new Decimal(0);
      let assetPart = Zero;
      let dCanUseFixed = Zero;
      if (positionModeSet === PositionModeEnum.ONE_WAY) {
        // 所有全仓币对维持保证金额和 = 所有全仓币对当前价值*(MM+爆仓手续费)
        let tokenTotalCrossedOneWayCrossMarginValue =
          tokenIdTotalValue.get(tokenId);
        maintainPart = tokenTotalCrossedOneWayCrossMarginValue;
        // 国立 确认过 逐仓保证金之前已经排除掉了
        // 分母：之前是available - 所有逐仓币对的仓位保证金 + 全仓未实现盈亏
        // 现在改成 available + 所有 逐仓仓位保证金 - 所有逐仓币对占用仓位保证金 + 全仓未实现盈亏
        assetPart = dAvailable
          .add(totalFixedMargin)
          .sub(fixedTotalMargin)
          .add(totalCrossedUpnl)
          .times(bp);
        dCanUseFixed = dCanUse;
        oneWayMaintainPart = maintainPart.div(bp);
      } else {
        // 保证金比率：分子（所有交易对大头仓位所需维持保证金之和+所有交易对开仓委托价值所需维持保证金之和）
        // 分母：Asset(available-逐仓开仓成本）+总未实现盈亏
        // 都按保证金记
        // 20220427 追加逐仓委托维持保证金额（委托量+对应持仓量 查询档位，委托量*对应档位的维持保证金率）
        maintainPart = totalCrossedMaintain
          .add(totalCrossedOpenMaintain)
          .add(fixedOrderMaintainValue);
        assetPart = dAvailable.add(totalCrossedUpnl);
        dCanUseFixed = dCanUse.isNegative() ? Zero : dCanUse;
      }

      // 维持保证金率 MMR
      let dRiskRate = Zero;
      if (!assetPart.isZero()) {
        dRiskRate = maintainPart.div(assetPart).times(100);
      }

      let maintainPart2 = Zero;
      let canLossMargin = Zero;

      let dMaintainPart =
        positionModeSet === PositionModeEnum.ONE_WAY
          ? oneWayMaintainPart
          : maintainPart;
      // todo 后续若开放其他业务线，则需要将 ['USDT', 'SUSDT'] 的判断去除
      if (isUnionMode && ["USDT", "SUSDT"].includes(tokenId)) {
        // 维持保证金2 = 负债绝对值*5%
        maintainPart2 = new Decimal(usdtDebt).times(ummr);
        const maxMaintainPart =
          dMaintainPart.comparedTo(maintainPart2) === 1
            ? dMaintainPart
            : maintainPart2;

        if (usdtDebt > 0) {
          // 维持保证金率mmr = max(维持保证金1, 维持保证金2)/联合保证金
          dRiskRate = maxMaintainPart.div(dProfits).times(100);
          dMaintainPart = maxMaintainPart;
        }

        // 负债的初始保证金 = 负债绝对值*10% ，参数可配置，后端配置定为10%
        // 可用 = 现在计算的可用 - 负债的初始保证金
        dCanUse = dCanUse.sub(new Decimal(usdtDebt).times(debtMRatio));
        dCanUseFixed = dCanUse;
        // 可亏保证金 = 组合保证金-max(维持保证金1, 维持保证金2)
        canLossMargin = dProfits.minus(maxMaintainPart);
      }

      let dCanUseFixedUI = dCanUse.isNegative() ? Zero : dCanUse;
      return {
        ...account,
        // 资产
        dProfits,
        // 可用
        dCanUseUI: dCanUseFixedUI,
        dCanUse: dCanUseFixed,
        dCanUseValue: dCanUseFixed.times(bp),
        dTotalUsed,
        dFreeze,
        dTotalPnl,
        dTotalUpnl,
        dRiskRate,
        dCanAppend,
        maintainPart: dMaintainPart,
        canLossMargin,
        maintainPart2,
      };
    });
  },
  set: ({ set, get }, data) => {
    const currentMargin = get(CurrentMarginSelector);

    if (!data) {
      // @ts-ignore
      data = get(MixContractBalanceListState);
    }

    if (Array.isArray(data) && data.length > 0) {
      // 因为有atom数据，需要解构
      data = [...data];

      const currentAccountIndex = data.findIndex(
        (account) => account.tokenId.toUpperCase() === currentMargin
      );

      if (currentAccountIndex !== -1) {
        // 因为有atom数据，需要解构
        data[currentAccountIndex] = {
          ...data[currentAccountIndex],
          available: 0,
        };
      }
    }

    set(MixContractBalanceListState, data);
  },
});

/**
 * getTotalUsdtValue   getTotalUsdtValueInquidationPrice 公用逻辑
 * 新增
 */
export const getTotalUsdtValueAndInquidationPriceCommon = selector({
  key: "get-total-usdt-value-inquidation-price",
  get: ({ get }) => {
    const tickers = get(MinifiedTickerListAtom);
    // const tickerKeyMap = get(MixContractTickerKeyMap)

    const orderListDefault = get(MixContractCurrentOrdersState);
    const positionListDefault = get(MixContractPositionListState);

    const contractList = get(CurrentBusinessLineContractListState);
    const levelListLoadable = get(
      noWait(CurrentBusinessLineContractPositionLevelListState)
    );
    const levelList =
      levelListLoadable.state === "hasValue" ? levelListLoadable.contents : [];

    return ({
      orderList = orderListDefault,
      positionList = positionListDefault,
    }) => {
      // !!! 注意：为了获取正确的仓位档位梯度，需要统计某保证金下某合约，持仓仓位价值（usdt记）
      // 以及委托价值（usdt记）的总和，再去合约档位梯度中读取维持保证金
      const tokenSymbolHoldUsdtValue = new StatisticResult();
      // !!! 注意：这里新起一个 除仓位大头价值有变化外 其他跟上面保持一样的逻辑
      const tokenSymbolHoldUsdtValueForMM = new StatisticResult();
      // 按照多空方向更细的统计 价值 （仓位 + 委托)
      const tokenSymbolSideUsdtValue = new StatisticResult();
      // 按照多空方向更细的统计 价值 （仓位 + 委托) 但是是全币对 单向持仓用
      const tokenSideUsdtValue = new StatisticResult();

      // 统计委托部分，委托部分是未成交数量*委托价格
      for (let order of orderList) {
        const {
          delegateType,
          delegateCount,
          dealCount,
          delegatePrice,
          tokenId,
          symbolId,
        } = order;
        // const unionKey = `${tokenId}_${symbolId}`
        const unionKey = generateUniKey(tokenId, symbolId);
        // 仅统计开仓委托
        // 下面单向持仓也是有问题的
        if (
          delegateType === MixContractDelegateType.OPEN_LONG ||
          delegateType === MixContractDelegateType.OPEN_SHORT ||
          delegateType === MixContractDelegateType.SELL_IN_SINGLE_SIDE_MODE ||
          delegateType === MixContractDelegateType.BUY_IN_SINGLE_SIDE_MODE
        ) {
          const holdSide = delegateType2Holdside(delegateType);

          // 仅统计限价委托，默认最优成交价的单子不会停留在当前委托中
          // 非单向持仓应该也没 只减仓这个字段
          if (delegatePrice && !order.onlyLow) {
            let delegateUsdtValue = new Decimal(delegateCount || 0)
              .sub(dealCount)
              .times(delegatePrice);

            tokenSymbolHoldUsdtValue.add(unionKey, delegateUsdtValue);
            tokenSymbolHoldUsdtValueForMM.add(unionKey, delegateUsdtValue);
            tokenSymbolSideUsdtValue.add(
              unionKey + "_" + holdSide,
              delegateUsdtValue
            );
            tokenSideUsdtValue.add(tokenId + "_" + holdSide, delegateUsdtValue);
          }
        }
      }

      const biggerPositionHoldValue = new StatisticResult();
      const biggerPositionHoldValueForMM = new StatisticResult();

      // 统计持仓部分，持仓部分是持仓量*最新合理标记价格
      for (let position of positionList) {
        const {
          tokenId,
          symbolId,
          available,
          locked,
          holdSide,
          averageOpenPrice,
        } = position;
        // const unionKey = `${tokenId}_${symbolId}`
        const unionKey = generateUniKey(tokenId, symbolId);
        const contractConfig = getContractConfig(contractList, symbolId);
        // 计算 fairPrice
        const fairPrice = getTicker(tickers, contractConfig)?.markPrice || "1";

        const holdUsdtValue = new Decimal(available)
          .add(locked)
          .times(fairPrice); // 仓位 最新标记价格计算的 价值

        const old = biggerPositionHoldValue.get(unionKey);
        // holdUsdtValue 大于 old 更新操作
        if (holdUsdtValue.greaterThan(old)) {
          biggerPositionHoldValue.set(unionKey, holdUsdtValue);
        }

        // 仓位大头*min(当时的标记价格，仓位大头开仓价格)+委托价值 来确定档位，进而找出对应的维持保证金比率
        const minPrice = Decimal.min(fairPrice, averageOpenPrice);
        const holdUsdtValueForMM = new Decimal(available)
          .add(locked)
          .times(minPrice);

        const oldForMM = biggerPositionHoldValueForMM.get(unionKey);
        // holdUsdtValue 大于 oldForMM 更新操作
        // 如果双向仓位一样，开仓价格取较高的 为了避免 都符合上面的 min的情况下 取出来的 holdUsdtValueForMM 相等，所以使用之前的逻辑既以开仓均价算出来的大头
        if (
          holdUsdtValue.greaterThan(old) &&
          holdUsdtValueForMM.greaterThan(oldForMM)
        ) {
          biggerPositionHoldValueForMM.set(unionKey, holdUsdtValueForMM);
        }

        tokenSymbolSideUsdtValue.add(unionKey + "_" + holdSide, holdUsdtValue);
        tokenSideUsdtValue.add(tokenId + "_" + holdSide, holdUsdtValue);
      }

      biggerPositionHoldValue.forEach((value, key) => {
        tokenSymbolHoldUsdtValue.add(key, value);
      });

      biggerPositionHoldValueForMM.forEach((value, key) => {
        tokenSymbolHoldUsdtValueForMM.add(key, value);
      });

      // 为了将要开的仓位 新增的仓位价值计算

      //  临时装载币对价值
      let SymbolNetValueMap = new Map<string, Set<string>>();

      // 单币对多头仓位价值 //全仓
      let symbolIdTotalLongPosValue = new StatisticResult();
      // 单币对空头仓位价值
      let symbolIdTotalShortPosValue = new StatisticResult();
      // 单币对多头仓位价值 专门用来取mm
      let symbolIdTotalLongPosValueForMM = new StatisticResult();
      // 单币对空头仓位价值 专门用来取mm
      let symbolIdTotalShortPosValueForMM = new StatisticResult();
      // 单币对多头委托价值 只算全仓不算逐仓
      let symbolIdTotalLongOrderValue = new StatisticResult();
      // 单币对空头委托价值 只算全仓不算逐仓
      let symbolIdTotalShortOrderValue = new StatisticResult();

      // 单向用
      // 币对当前价值: 所有全仓币对当前价值*(MM+爆仓手续费) 不包含逐仓  加入了将要开仓的部分
      let tokenIdTotalValue = new StatisticResult();
      // 单向用
      // 币对占用价值 的大头
      let tokenIdTotalValueMax = new StatisticResult();
      // 币对占用价值 的大头
      let tokenIdTotalValueMaxForMM = new StatisticResult();

      let totalSymbols = new Set<string>();

      //所有的委托 包含止盈止损委托 不区分全仓逐仓
      let allOrderList: MixContractCurrentOrderFixedData[] =
        formatCurrentOrders(get, orderList);
      allOrderList.forEach((item) => {
        //止盈止损委托是没有 dealCount 字段 所以可以用这种方法去掉
        if (!item.onlyLow && item.dealCount) {
          let delegateUsdtValue = new Decimal(item.delegateCount || 0)
            .sub(item.dealCount)
            .times(item.delegatePrice || 0);
          const unionKey = generateUniKey(item.tokenId, item.symbolId);
          //不区分全仓逐仓
          if (
            delegateType2Holdside(item.delegateType) ===
            MixContractHoldSide.LONG_POSITIONS
          ) {
            if (item.marginMode !== MixContractMarginMode.FIXED_MARGIN) {
              //只算全仓 不算逐仓
              symbolIdTotalLongOrderValue.add(unionKey, delegateUsdtValue);
            }
          } else {
            //只算全仓 不算逐仓
            if (item.marginMode !== MixContractMarginMode.FIXED_MARGIN) {
              symbolIdTotalShortOrderValue.add(unionKey, delegateUsdtValue);
            }
          }
          totalSymbols.add(item.symbolId);
          SymbolNetValueMap.set(item.tokenId, totalSymbols);
        }
      });

      //
      for (let position of positionList) {
        const {
          tokenId,
          symbolId,
          available,
          locked,
          holdSide,
          averageOpenPrice,
        } = position;

        // const unionKey = `${tokenId}_${symbolId}`
        const unionKey = generateUniKey(tokenId, symbolId);
        const contractConfig = getContractConfig(contractList, symbolId);
        const fairPrice = getTicker(tickers, contractConfig)?.markPrice || "1";
        const holdUsdtValue = new Decimal(available)
          .add(locked)
          .times(fairPrice); // 仓位 最新标记价格计算的 价值
        const minPrice = Decimal.min(fairPrice, averageOpenPrice);
        const holdUsdtValueForMM = new Decimal(available)
          .add(locked)
          .times(minPrice);
        //这里只算全仓 不包含逐仓
        if (position.marginMode !== MixContractMarginMode.FIXED_MARGIN) {
          if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
            symbolIdTotalLongPosValue.add(unionKey, holdUsdtValue);
            symbolIdTotalLongPosValueForMM.add(unionKey, holdUsdtValueForMM);
          } else {
            symbolIdTotalShortPosValue.add(unionKey, holdUsdtValue);
            symbolIdTotalShortPosValueForMM.add(unionKey, holdUsdtValueForMM);
          }
        }

        totalSymbols.add(symbolId);
        SymbolNetValueMap.set(tokenId, totalSymbols);
      }
      //这里有两处都要同步做修改 OneWayPosition.ts
      //币对价值
      SymbolNetValueMap.forEach((symbols, tokenId) => {
        for (let item of symbols) {
          const unionKey = `${tokenId}_${item}`;
          let longPos = symbolIdTotalLongPosValue.get(unionKey);
          let shortPos = symbolIdTotalShortPosValue.get(unionKey);
          let longOrder = symbolIdTotalLongOrderValue.get(unionKey);
          let shortOrder = symbolIdTotalShortOrderValue.get(unionKey);
          // 单向的
          // 币对当前价值=max(多头仓位价值+多头委托价值, 空头仓位价值+空头委托价值)
          let final = Math.max(
            longPos.add(longOrder).toNumber(),
            shortPos.add(shortOrder).toNumber()
          );

          // 取mm
          let longPosForMM = symbolIdTotalLongPosValueForMM.get(unionKey);
          let shortPosForMM = symbolIdTotalShortPosValueForMM.get(unionKey);
          let finalForMM = Math.max(
            longPosForMM.add(longOrder).toNumber(),
            shortPosForMM.add(shortOrder).toNumber()
          );
          tokenIdTotalValueMaxForMM.add(unionKey, finalForMM);
          //下面部分归单向用
          const contractConfig = getContractConfig(contractList, item);
          const contractLevelList =
            levelList.find((l) => l.symbolId === contractConfig?.symbolId)
              ?.levels || [];
          let levelInfo = getLevel(contractLevelList, finalForMM);
          let feeRate = contractConfig.feeRate;
          let mm = levelInfo.keepMarginRate;
          let fee = new Decimal(final)
            .times(new Decimal(mm).add(new Decimal(feeRate)))
            .toNumber();
          tokenIdTotalValue.add(tokenId, fee);
          tokenIdTotalValueMax.add(unionKey, final);
        }
      });
      return {
        tokenSymbolHoldUsdtValue,
        tokenSymbolHoldUsdtValueForMM,
        tokenSymbolSideUsdtValue,
        tokenSideUsdtValue,
        tokenIdTotalValue,
        tokenIdTotalValueMax,
        tokenIdTotalValueMaxForMM,
      };
    };
  },
});

/** 备份代码在下面
 * 统计某保证金下某合约总共持有价值，包含仓位价值以及委托价值
 * 仓位价值用最新合理标记价格算，委托价值用委托价格算
 * 结果单位为USDT，用于在仓位档位中寻找梯度
 */
export const getTotalUsdtValue = selector({
  key: "get-mm",
  get: ({ get }) => {
    const getTotalUsdtValueAndInquidationPriceCommonFn = get(
      getTotalUsdtValueAndInquidationPriceCommon
    );
    const {
      tokenSymbolHoldUsdtValue,
      tokenSymbolHoldUsdtValueForMM,
      tokenSymbolSideUsdtValue,
      tokenSideUsdtValue,
    } = getTotalUsdtValueAndInquidationPriceCommonFn({});
    return {
      tokenSymbolHoldUsdtValue,
      tokenSymbolHoldUsdtValueForMM,
      tokenSymbolSideUsdtValue,
      tokenSideUsdtValue,
    };
  },
});

/**
 * judgeOutput(a, b, c) 这个的意思是如果 a>0, 结果为b，否则为c
 * */
export const judgeOutput = (condition: Decimal, a: Decimal, b: Decimal) => {
  return condition.greaterThan(0) ? a : b;
};

// ================================================================
/**
 * 计算开仓成本价
 */
export const OpenPriceUpper = selector({
  key: "open-price-upper",
  get: ({ get }) => {
    const contractConfig = get(MixContractInfoState);
    const contractConfigMap = get(MixContractInfoMapSelector);

    return (
      price: Decimal.Value,
      leverage: Decimal.Value,
      symbolId?: string
    ) => {
      let dPrice = new Decimal(price);
      if (dPrice.isZero()) return Zero;
      let config = symbolId
        ? contractConfigMap.get(symbolId) || createDefaultMixContractInfo()
        : contractConfig || createDefaultMixContractInfo();
      let { openCostUpRatio, feeRate, feeRateUpRatio } = config;
      return dPrice.times(
        new Decimal(openCostUpRatio)
          .add(1)
          .div(leverage)
          .add(new Decimal(feeRate).times(new Decimal(feeRateUpRatio).add(1)))
      );
    };
  },
});

// 6.7 改
//全仓
// 多头可开资金 = 可用+该币对多头补偿保证金 - 当前币对空头仓位数量*当前标记价格*0.0006
// 空头可开资金 = 可用+该币对空头补偿保证金 - 当前币对多头仓位数量*当前标记价格*0.0006
//逐仓
// 多头可开资金 = 可用+该币对多头补偿保证金 - (当前币对空头仓位数量*当前标记价格*0.0006)/指数价格 + 当前币对空头未实现盈亏
// 空头可开资金 = 可用+该币对空头补偿保证金 - (当前币对多头仓位数量*当前标记价格*0.0006)/指数价格 + 当前币对多头未实现盈亏

// 计算最大可开-旧版
export const getCanMaxOpenCalculator = selector({
  key: "cal-max-can-open",
  get: ({ get }) => {
    const contractConfig = get(MixContractInfoState);
    const contractConfigMap = get(MixContractInfoMapSelector);
    const { tokenSymbolHoldUsdtValue, tokenSymbolSideUsdtValue } =
      get(getTotalUsdtValue);
    const priceUpper = get(OpenPriceUpper);
    const positionModeSet = get(UserPreferenceSettingState).positionModeSet;
    const currentMargin = get(CurrentMarginSelector);
    const canCloseInfo = get(CurrentContractCanCloseSelector(currentMargin));
    const isReducePosition = get(OPOnlyReducePositionPresetState); // 只减仓
    // 仓位聚合的数据
    const orderType = get(OPTypeState);

    // 是否是分段单向持仓 https://bglimited.larksuite.com/docx/U1hsdBlFro2chexqQytu7H3Gsh5
    const isScaledOneWay =
      orderType === OrderTypeEnum.SCALED &&
      positionModeSet === PositionModeEnum.ONE_WAY;

    /**
     * 可用价值
     * 使用价格
     * 杠杆
     * 保证金模式
     * 持仓方向
     */
    return (
      canUseValue: Decimal.Value,
      price: Decimal.Value,
      leverage: Decimal.Value,
      marginMode: MixContractMarginMode,
      holdSide: MixContractHoldSide,
      symbolId?: string,
      isCostValue?: boolean, // 是否是成本价值
      currOrderFreezeD: Decimal = Zero, // 传入这个参数代表在计算撤销委托单后的可开 冻结的可开
      delegateUsdtValue: Decimal = Zero, // 传入这个参数代表在计算撤销委托单后的可开 冻结的资金
      tokenId = "" // 传入这个参数代表在计算撤销委托单后的可开 撤销委托单的保证金币种
    ): Decimal => {
      if (new Decimal(price).isZero()) return Zero;
      const debugTag = currOrderFreezeD.greaterThan(0) ? "CANCLE:" : " OP";
      const tickers = get(MinifiedTickerListAtom);

      const {
        tokenLongTotalUpnl,
        tokenShortTotalUpnl,
        tokenTotalCrossedPositionUnrealizedProfit,
        tokenTotalFixedPositionMargin,
      } = get(MixContractPositionStatisticSelector);
      if (!tokenId) {
        tokenId = get(CurrentMarginSelector);
      }

      // canUseValue 这里加入单向持仓的计算
      let finalCanUseD = new Decimal(canUseValue);
      let config = symbolId ? contractConfigMap.get(symbolId) : contractConfig;
      const contractLevelList = get(ContractPositionLevelList(config.symbolId));
      const bp = get(getTokenPriceByTokenId(tokenId)).indexPrice;
      const {
        symbolIdTotalMargin,
        tokenIdTotalMargin,
        symbolIdTotalShortOrderMargin,
        symbolIdTotalShortPosMargin,
        symbolIdTotalLongOrderMargin,
        symbolIdTotalLongPosMargin,
        symbolIdCurTotalLongPosMargin,
        symbolIdCurTotalShortPosMargin,
        symbolIdTotalLongPosNum,
        symbolIdTotalShortPosNum,
      } = get(MixOneWayCalculateOrdersMargin);

      //成本价值的时候使用传进来的canUse来计算即可 不需要额外计算
      if (!isCostValue && positionModeSet === PositionModeEnum.ONE_WAY) {
        let symbolId = config.symbolId;
        const unionKey = generateUniKey(tokenId, symbolId);
        let longPosMargin = symbolIdTotalLongPosMargin.get(unionKey).times(bp); // 单币对多头仓位保证金（dMargin）
        let longOrderMargin = symbolIdTotalLongOrderMargin
          .get(unionKey)
          .times(bp);
        let longCurPosMargin = symbolIdCurTotalLongPosMargin
          .get(unionKey)
          .times(bp);

        let shortPosMargin = symbolIdTotalShortPosMargin
          .get(unionKey)
          .times(bp); // 单币对空头仓位保证金（dMargin）
        let shortOrderMargin = symbolIdTotalShortOrderMargin
          .get(unionKey)
          .times(bp);
        let shortCurPosMargin = symbolIdCurTotalShortPosMargin
          .get(unionKey)
          .times(bp);

        // 该币对占用保证金
        // 计算撤销委托单后的可开需要重新计算offsetMargin（该币对占用保证金）
        let offsetMarginBP;
        let canUseWithCancelBP = new Decimal(canUseValue); // 考虑撤单的可用

        const currOrderFreezeUSDTD = new Decimal(currOrderFreezeD).times(bp);
        // 撤单情况重新计算可用
        if (currOrderFreezeD.greaterThan(0)) {
          // if (1) {
          // const collector = get(PositionCollectorV1)
          // const positions = get(MixContractPositionListState)
          // const currTokenIdPosition = positions.filter(p => p.tokenId === tokenId)
          // const transfer = get(PositionTransfer)
          // const transferCurrTokenIdPosition = currTokenIdPosition.map(transfer)
          // const computeInquidationPriceFn = get(
          //     getTotalUsdtValueAndInquidationPriceCommon
          // ) // 计算价值用  Account.ts

          // const {
          //     tokenSymbolHoldUsdtValueForMM,
          //     tokenSymbolSideUsdtValue,
          //     tokenSideUsdtValue
          // } = computeInquidationPriceFn({
          //     positionList: transferCurrTokenIdPosition
          // })

          // const {
          //     tokenSymbolCrossedMaintain // 某币对维持保证金
          // } = collector(
          //     transferCurrTokenIdPosition,
          //     tokenSymbolHoldUsdtValueForMM,
          //     tokenSymbolSideUsdtValue,
          //     tokenSideUsdtValue
          // )
          // const { tokenIdTotalMargin, symbolIdTotalMargin } = get(MixOneWayCalculateOrdersMargin)

          const totalCrossedUpnl =
            tokenTotalCrossedPositionUnrealizedProfit.get(tokenId);

          const totalFixedMargin = tokenTotalFixedPositionMargin.get(tokenId);

          let res;
          if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
            res = Math.max(
              longPosMargin
                .add(longOrderMargin)
                .sub(currOrderFreezeUSDTD)
                .toNumber(),
              shortPosMargin.add(shortOrderMargin).toNumber()
            );
          } else {
            res = Math.max(
              longPosMargin.add(longOrderMargin).toNumber(),
              shortPosMargin
                .add(shortOrderMargin)
                .sub(currOrderFreezeUSDTD)
                .toNumber()
            );
          }

          offsetMarginBP = new Decimal(res);

          /**
           *
           * - 该币对占用保证金(new) = max(同向仓位保证金 + 同向委托保证金 - A， 反向仓位保证金 + 反向委托保证金)
           * - 所有币对的占用保证金(new)  = 其他币对占用保证金之和 + 该币对占用保证金(new)
           * - 可用(new)  = 可用余额 available + A - 所有币对的占用保证金(new) + 全仓未实现盈亏
           *
           * */

          const tokenTotalMargin = tokenIdTotalMargin.get(tokenId); // 所有币对占用保证金(当前保证金单位) 撤单前
          const currSymbolTotalMargin = symbolIdTotalMargin.get(unionKey); // 该币对占用保证金(当前保证金单位) 撤单前
          const otherSymbolsMargin = tokenTotalMargin.sub(
            currSymbolTotalMargin ?? 0
          ); // 其他币对占用保证金 撤单前 & 撤单后
          const otherSymbolsMarginBP = otherSymbolsMargin.times(bp); // 其他币对占用保证金 撤单前 & 撤单后
          const totalMarginWithCancelBP =
            offsetMarginBP.add(otherSymbolsMarginBP); // 重新计算的所有币对占用保证金 撤单后

          const account = get(getFixedUserAccountByTokenId(tokenId));
          // const opAssetsCanUse = account.dCanUseUI
          // const available = account.available

          /**
           *
           * */

          /***
           *
           *  参考 MixContractBalanceListSelector
           *    dCanUse = dAvailable
           *                     .sub(totalMargin) //所有币对的占用保证金
           *                     .add(totalCrossedUpnl)
           *                     .add(totalFixedMargin)
           */

          /**
           * {
           *   currOrderFreezeD: 1450.08864;
           *   dAvailable: 999.54636912;
           *   totalCrossedUpnl: -0.042;
           *   totalFixedMargin: 0;
           *   totalMarginWithCancel: 453.5958;
           * }
           * */

          const { unionAssetAvailable, debtMRatio } = get(
            MixContractUnionAssetPrice
          );
          const usdtDebt = get(MixContractUnionUsdtDebt);
          let dAvailable = new Decimal(account.available);
          const isUnionMode = get(ContractIsUnionModeSelector);

          // todo 后续若开放其他业务线，则需要将 ['USDT', 'SUSDT'] 的判断去除
          if (isUnionMode && ["USDT", "SUSDT"].includes(tokenId)) {
            dAvailable = dAvailable
              .add(unionAssetAvailable)
              .sub(new Decimal(usdtDebt).times(debtMRatio));
          }

          const noBP = dAvailable.add(totalCrossedUpnl).add(totalFixedMargin);

          canUseWithCancelBP = noBP.times(bp).sub(totalMarginWithCancelBP);
        } else {
          offsetMarginBP = symbolIdTotalMargin.get(unionKey).times(bp);
        }

        // 多头补偿保证金 = clamp(多头仓位保证金+多头委托所需保证金-空头仓位保证金-空头委托所需保证金, 该币对占用保证金, 该币对占用保证金+该币对空头仓位保证金)
        // 空头补偿保证金 = clamp(空头仓位保证金+空头委托所需保证金-多头仓位保证金-多头委托所需保证金, 该币对占用保证金, 该币对占用保证金+该币对多头仓位保证金)
        // calmp(a-b,1,-1)这个的意思是如果a-b>0,结果为1，否则为-1
        //4.17 改
        // 该币对多头补偿保证金 = clamp(该币对多头当前仓位保证金+该币对多头委托所需保证金-该币对空头当前仓位保证金-该币对空头委托所需保证金,
        // 该币对占用保证金-该币对多头仓位保证金-该币对多头委托所用保证金,
        // 该币对占用保证金+该币对当前空头仓位保证金-该币对多头当前仓位保证金-该币对多头委托所用保证金)
        // 该币对空头补偿保证金 = clamp(该币对空头当前仓位保证金+该币对空头委托所需保证金-该币对多头当前仓位保证金-该币对多头委托所需保证金, 该币对占用保证金-该币对空头仓位保证金-该币对空头委托所用保证金, 该币对占用保证金+该币对多头当前仓位保证金-该币对空头当前仓位保证金-该币对空头委托所用保证金)
        // calmp(a-b,1,-1)这个的意思是如果a-b>0,结果为1，否则为-1
        const shortPosNum = symbolIdTotalShortPosNum.get(unionKey);
        const longPosNum = symbolIdTotalLongPosNum.get(unionKey);
        const markPrice = getTicker(tickers, config)?.markPrice || "1";

        let longCompensateMargin, shortCompensateMargin;
        if (isScaledOneWay) {
          // 分段单向 补偿保证金任何时候均是，该币对占用保证金
          longCompensateMargin = offsetMarginBP
            .sub(longPosMargin)
            .sub(longOrderMargin);
          shortCompensateMargin = offsetMarginBP
            .sub(shortPosMargin)
            .sub(shortOrderMargin);
        } else {
          const res = calcCompensateMargin({
            longCurPosMargin,
            longPosMargin,
            longOrderMargin,
            holdSide,
            offsetMargin: offsetMarginBP,
            currOrderFreezeUSDTD,
            shortCurPosMargin,
            shortPosMargin,
            shortOrderMargin,
          });
          longCompensateMargin = res.longCompensateMargin;
          shortCompensateMargin = res.shortCompensateMargin;
        }

        let reCalcedCanUse;

        if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
          reCalcedCanUse = calcalteLongNewDCanUseValue(
            marginMode,
            canUseWithCancelBP,
            longCompensateMargin,
            shortPosNum,
            markPrice,
            tokenShortTotalUpnl.get(unionKey),
            config.feeRate,
            bp
          );
        } else {
          reCalcedCanUse = calcalteLongNewDCanUseValue(
            marginMode,
            canUseWithCancelBP,
            shortCompensateMargin,
            longPosNum,
            markPrice,
            tokenLongTotalUpnl.get(unionKey),
            config.feeRate,
            bp
          );
        }

        finalCanUseD = reCalcedCanUse;
      }

      // 由杠杆统计仓位档位的固有限制
      let defaultMax = 0;
      let levels = contractLevelList || [];
      const level = findLastIndex(levels, (l) => +leverage <= l.leverage);

      if (level) {
        defaultMax = level.endUnit;
      }

      // 已有
      let hold: Decimal;

      if (marginMode === MixContractMarginMode.FIXED_MARGIN) {
        hold = tokenSymbolSideUsdtValue.get(
          `${tokenId}_${config.symbolId}_${holdSide}`
        );
      } else {
        hold = tokenSymbolHoldUsdtValue.get(`${tokenId}_${config.symbolId}`);
      }
      //与上面逐仓一样 单独写一下
      if (positionModeSet === PositionModeEnum.ONE_WAY) {
        hold = tokenSymbolSideUsdtValue.get(
          `${tokenId}_${config.symbolId}_${holdSide}`
        );
      }

      // 在固有限制下，除掉已持有部分，还能再持有的上限 如果有撤销委托单，上限需要加上撤销委托单的冻结金额 重新计算剩余可持有

      // const currOrderFreezeBPD = currOrderFreezeD.times(bp).times(leverage)

      const rest = new Decimal(defaultMax)
        .sub(hold)
        .add(delegateUsdtValue)
        .div(price);

      // 按照开仓成本计算，可用部分能够开仓
      let canMaxOpen;
      if (isReducePosition) {
        //只减仓
        if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
          canMaxOpen = new Decimal(canCloseInfo.canCloseShort);
        } else {
          canMaxOpen = new Decimal(canCloseInfo.canCloseLong);
        }
      } else {
        // 按照开仓成本计算，可用部分能够开仓
        canMaxOpen = finalCanUseD.div(priceUpper(price, leverage, symbolId));
      }

      // 但是上述计算结果能超过档位限制的剩余部分，如果超过了，做一个上限处理
      if (canMaxOpen.greaterThan(rest)) {
        canMaxOpen = rest;
      }
      if (canMaxOpen.isNegative()) {
        canMaxOpen = Zero;
      }

      return canMaxOpen;
    };
  },
});

//获取阶梯最大可开-新版-非计算
export const getCanMaxOpenNewNoCal = selector({
  key: "can-max-open-new-no-cal",
  get: ({ get }) => {
    const contractConfig = get(MixContractInfoState);
    const contractConfigMap = get(MixContractInfoMapSelector);
    return ({ leverage, symbolId }): number => {
      const config = symbolId
        ? contractConfigMap.get(symbolId)
        : contractConfig;
      const contractLevelList = get(ContractPositionLevelList(config.symbolId));
      const levels = contractLevelList || [];
      const level = findLastIndex(levels, (l) => +leverage <= l.leverage);
      let defaultMax = 0;
      if (level) {
        defaultMax = level.endUnit;
      }
      return defaultMax;
    };
  },
});

export const getOpenCostCalculator = selector({
  key: "cal-open-cost",
  get: ({ get }) => {
    const contractConfig = get(MixContractInfoState);
    const tokenId = get(CurrentMarginSelector);
    const bp = get(getTokenPriceByTokenId(tokenId)).indexPrice;
    return (
      calPrice: Decimal.Value,
      calAmount: Decimal.Value,
      leverage: Decimal.Value
    ) => {
      let cost = calOpenCost(
        calPrice ?? 0,
        calAmount ?? 0,
        bp,
        leverage,
        contractConfig.openCostUpRatio,
        contractConfig.feeRate,
        contractConfig.feeRateUpRatio
      );

      return cost.toString();
    };
  },
});

/**
 * 杠杆变更的情况下保证金的情况
 */
export const getMarginConditionCalculator = selector({
  key: "cal-margin-condition",
  get: ({ get }) => {
    // !!! 有委托时不能调整杠杆，所以暂时不考虑委托
    const positionList = get(MixContractBaseFixedPositionListSelector);
    const account = get(MixContractUserAccountSelector);

    return (
      symbolId: string,
      tokenId: string,
      leverage: Decimal.Value,
      marginMode: MixContractMarginMode,
      holdSide: MixContractHoldSide
    ) => {
      // 新杠杆下所需保证金
      let marginNew = Zero;
      // 杠杆调小时，需要追加的保证金；杠杆调大时，释放占用的保证金
      let marginChange = Zero;
      // 是否有足够的金额可以调整
      let canAdjust = true;

      for (let position of positionList) {
        // 根据合约和保证金进行区分
        if (position.symbolId === symbolId && position.tokenId === tokenId) {
          let tempMargin = position.dOpenValue.div(leverage);
          if (marginMode === MixContractMarginMode.FIXED_MARGIN) {
            // 逐仓只用判断目标仓位内部的情况
            if (position.holdSide === holdSide) {
              marginNew = tempMargin;

              marginChange = marginNew.sub(position.dMargin);
              if (position.dUpnl.isNegative()) {
                marginChange = marginChange.add(position.dUpnl);
              }
              if (marginChange.isNegative()) {
                marginChange = Zero;
              }
            }
          } else {
            // 全仓判断大头仓位
            if (tempMargin.greaterThan(marginNew)) {
              marginNew = tempMargin;
              marginChange = marginNew.sub(position.dMargin);
            }
          }
        }
      }

      if (
        marginChange.greaterThan(0) &&
        account.dCanUse.lessThan(marginChange)
      ) {
        // 需要追加金额，且可用不足时
        canAdjust = false;
      }

      return { marginChange, marginNew, canAdjust };
    };
  },
});

/**
 * 获取某保证金的资产信息
 */
export const getFixedUserAccountByTokenId = selectorFamily<
  MixContractFixedUserAccountInfo,
  string
>({
  key: "get-fixed-user-account-selector",
  get:
    (tokenId: string) =>
    ({ get }) => {
      const accounts = get(MixContractBalanceListSelector);

      return (
        accounts.find((account) => account.tokenId === tokenId) ||
        createEmptyAccountInfo()
      );
    },
});

/**
 * 获取当前保证金的资产情况
 */
export const MixContractUserAccountSelector =
  selector<MixContractFixedUserAccountInfo>({
    key: "single-user-account-selector-public",
    get: ({ get }) => {
      const balanceList = get(MixContractBalanceListSelector);
      const currentMargin = get(CurrentMarginSelector);
      const result = balanceList.find(
        (a) => a.tokenId.toUpperCase() === currentMargin
      );
      return result || createEmptyAccountInfo();
    },
  });

const keepDRiskRateRef = createRefKeeper<Decimal>((a, b) => a.eq(b));
/**
 * 获取当前保证金的资产情况 - dRiskRate
 */
export const MixContractUserAccountDRiskRateSelector = selector<
  MixContractFixedUserAccountInfo["dRiskRate"]
>({
  key: "single-user-account-d-risk-rate-selector-public",
  get: ({ get }) => {
    return keepDRiskRateRef(get(MixContractUserAccountSelector).dRiskRate);
  },
});

const keepMaintainPartRef = createRefKeeper<Decimal>((a, b) => a.eq(b));
/**
 * 获取当前保证金的资产情况 - maintainPart
 */
export const MixContractUserAccountMaintainPartSelector = selector<
  MixContractFixedUserAccountInfo["maintainPart"]
>({
  key: "single-user-account-maintain-part-selector-public",
  get: ({ get }) => {
    return keepMaintainPartRef(
      get(MixContractUserAccountSelector).maintainPart
    );
  },
});

export type MixBalanceModeInfo = {
  businessLine: number; // 业务线，具体后面会给出枚举
  secondBusinessLine: string;
  tokenId: string; // 保证金id
  symbolId: string; // 交易对信息
  crossMarginLeverage: number; // 全仓杠杆
  fixedLongLeverage: number; // 逐仓多仓杠杆
  fixedShortLeverage: number; // 逐仓空仓杠杆
  marginMode: MixContractMarginMode; // 保证金模式
  holdMode: number; // 持仓模式 单向 双向
  fixedLongAutoMargin: number; //双向多头是否自动追加保证金
  fixedShortAutoMargin: number; //双向空头是否自动追加保证金
  singleHoldAutoMargin: number; //单项持仓
  assetsMode: USDT_ASSET_MODE_TYPE; // 单币种保证金、联合保证金
};

export function createEmptyAssetMode(): MixBalanceModeInfo {
  return {
    businessLine: 0,
    crossMarginLeverage: 20,
    fixedLongLeverage: 20,
    fixedShortLeverage: 20,
    holdMode: 1,
    marginMode: 1,
    secondBusinessLine: "",
    symbolId: "",
    tokenId: "",
    fixedLongAutoMargin: 0,
    fixedShortAutoMargin: 0,
    singleHoldAutoMargin: 0,
  };
}

/**
 * 各个保证金模式
 * 更新模式：增量
 */
export const MixContractUserAccountModesState = atom<MixBalanceModeInfo[]>({
  key: "user-assets-modes-state",
  default: [],
});

export const MixContractUserAccountModesSelector = selector<
  MixBalanceModeInfo[]
>({
  key: "user-assets-modes-selector",
  get: ({ get }) => {
    return get(MixContractUserAccountModesState);
  },
  set: ({ get, set }, newValue: MixBalanceModeInfo[]) => {
    const oldModes = get(MixContractUserAccountModesState);
    let temp = [];

    if (oldModes.length <= 0) {
      set(MixContractUserAccountModesState, newValue);
      return;
    }

    for (let mode of oldModes) {
      let find = newValue.find((newMode) => {
        return (
          newMode.symbolId === mode.symbolId && newMode.tokenId === mode.tokenId
        );
      });

      if (find) {
        temp.push(find);
      } else {
        temp.push(mode);
      }
    }

    set(MixContractUserAccountModesState, temp);
  },
});

/**
 * 拿取最具体的持仓模式信息
 */
export const getUserAccountMode = selectorFamily<
  MixBalanceModeInfo,
  { tokenId: string; symbolId: string }
>({
  key: "get-user-account-mode",
  get:
    (param) =>
    ({ get }) => {
      const { tokenId, symbolId } = param;
      const modes = get(MixContractUserAccountModesState);

      return (
        modes.find(
          (mode) => mode.tokenId === tokenId && mode.symbolId === symbolId
        ) || createEmptyAssetMode()
      );
    },
});

/**
 * 用户正在使用的保证金是什么模式
 */
export const MixContractUserAccountModeSelector = selector<MixBalanceModeInfo>({
  key: "user-asset-mode-selector",
  get: ({ get }) => {
    const currentMargin = get(CurrentMarginSelector);
    const ct = get(MixContractInfoState);
    const modes = get(MixContractUserAccountModesState);
    return (
      modes.find(
        (asset) =>
          String(asset.tokenId).toUpperCase() === currentMargin &&
          asset.symbolId === ct.symbolId
      ) || createEmptyAssetMode()
    );
  },
});

/**
 * 用户正在使用的保证金是什么模式 ---- strategy
 */
export const MixStrategyUserAccountModeSelector = selector<MixBalanceModeInfo>({
  key: "strategy-user-asset-mode-selector",
  get: ({ get }) => {
    const currentMargin = get(CurrentMarginSelector);
    const ct = get(MixStrategyInfoState);
    const modes = get(MixContractUserAccountModesState);
    return (
      modes.find(
        (asset) =>
          String(asset.tokenId).toUpperCase() === currentMargin &&
          asset.symbolId === ct.symbolId
      ) || createEmptyAssetMode()
    );
  },
});
